/**
 * Ιστορικό εκτελέσεων: εκτελέσεις → εργασίες ανά κατάστημα → κλήσεις (§13.11).
 *
 * ΓΙΑΤΙ φαίνονται οι κλήσεις μία-μία: κάθε εγγραφή στο efood γυρίζει γυμνό 200
 * χωρίς αποτέλεσμα ανά γραμμή (§13.6.4) — η μόνη απόδειξη του τι έγινε είναι η
 * καταγραφή της κλήσης μαζί με το verify που ακολούθησε. Μια «έκβαση: άγνωστη»
 * (502 γέφυρας ή timeout) ΔΕΝ είναι αποτυχία και πρέπει να ξεχωρίζει οπτικά, γιατί
 * απαγορεύεται η τυφλή επανάληψη.
 */

import React from 'react';
import { ChevronRight, History, Loader2 } from 'lucide-react';
import { RunDetail, RunSummary } from '../../services/platformCatalogApi';
import { fmtDateTime } from './catalogUi';

interface Props {
  runs: RunSummary[];
  loading: boolean;
  selectedId: number | null;
  detail: RunDetail | null;
  detailLoading: boolean;
  onSelect: (runId: number) => void;
  /** org_admin (§13.12) — μόνο αυτός ακυρώνει εκτέλεση. */
  canCancel: boolean;
  onCancel: (runId: number) => void;
}

/** Εκτέλεση που τρέχει τώρα: η λίστα ανανεώνεται μόνη της ανά 2΄΄ (§13.11). */
const isLive = (run: RunSummary): boolean => run.status === 'pending' || run.status === 'running';

const STATUS_CLASSES: Record<string, string> = {
  pending: 'bg-surface-3 text-t3',
  running: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  done: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  failed: 'bg-red-500/15 text-red-600 dark:text-red-400',
  halted: 'bg-red-500/20 text-red-700 dark:text-red-300',
  cancelled: 'bg-surface-3 text-t3',
};

const OUTCOME_CLASSES: Record<string, string> = {
  ok: 'text-emerald-700 dark:text-emerald-300',
  failed: 'text-red-600 dark:text-red-400',
  unknown: 'text-amber-700 dark:text-amber-300',
};

const RunHistory: React.FC<Props> = ({
  runs, loading, selectedId, detail, detailLoading, onSelect, canCancel, onCancel,
}) => (
  <div className="border border-line rounded-xl p-4 space-y-3">
    <h4 className="text-sm font-semibold text-t1 flex items-center gap-2">
      <History className="w-4 h-4 text-t3" /> Ιστορικό εκτελέσεων
      {loading && <Loader2 className="w-4 h-4 animate-spin text-t3" />}
      {runs.some(isLive) && <span className="text-[11px] text-t3">(ανανεώνεται ανά 2΄΄)</span>}
    </h4>

    {/* ΚΟΚΚΙΝΗ ΜΠΑΡΑ σε `halted`: η εκτέλεση σταμάτησε ΟΛΟΚΛΗΡΗ (ύποπτη
        διπλοεγγραφή, παράπλευρη αλλαγή ή ανεπαλήθευτο καναρίνι — §13.6.5) και
        καμία επανάληψη δεν επιτρέπεται πριν διαβαστεί ο λόγος. */}
    {runs.filter(r => r.status === 'halted').map(r => (
      <div key={`halted-${r.id}`} className="p-2.5 rounded-lg border border-red-400 bg-red-50 dark:bg-red-900/30 text-xs text-red-800 dark:text-red-200">
        Η εκτέλεση #{r.id} <strong>σταμάτησε</strong>
        {r.halted_reason ? `: ${r.halted_reason}` : ''}. Καμία τυφλή επανάληψη — πρώτα επαλήθευση από το efood.
      </div>
    ))}

    {runs.length === 0 && !loading && (
      <p className="text-sm text-t4">Καμία εκτέλεση ακόμη. Οι στεγνές δοκιμές που τρέχεις εδώ δεν γράφουν εκτέλεση.</p>
    )}

    <div className="space-y-1">
      {runs.map(run => (
        <div key={run.id}>
          <div className="flex items-stretch gap-1">
          <button
            type="button"
            onClick={() => onSelect(run.id)}
            className={`flex-1 min-w-0 flex flex-wrap items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm border
              ${selectedId === run.id ? 'bg-surface-1 border-line-strong' : 'bg-surface-2 border-line hover:bg-surface-1/60'}`}
          >
            <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${selectedId === run.id ? 'rotate-90' : ''}`} />
            <span className="text-t2">#{run.id}</span>
            <span className="text-t3 text-xs">{fmtDateTime(run.created_at)}</span>
            <span className="px-1.5 py-0.5 rounded text-[11px] bg-surface-3 text-t2">
              {run.mode === 'live' ? 'ζωντανή' : 'στεγνή'} · {run.kind === 'probe' ? 'probe' : 'αποστολή'}
            </span>
            <span className={`px-1.5 py-0.5 rounded text-[11px] ${STATUS_CLASSES[run.status] || 'bg-surface-3 text-t3'}`}>
              {run.status}
            </span>
            {run.stores_total !== undefined && run.stores_total !== null && (
              <span className="text-xs text-t3">{run.stores_total} καταστήματα</span>
            )}
            {run.edits_total !== undefined && run.edits_total !== null && (
              <span className="text-xs text-t3">{run.edits_total} αλλαγές</span>
            )}
            {run.halted_reason && (
              <span className="text-xs text-red-600 dark:text-red-400">σταμάτησε: {run.halted_reason}</span>
            )}
            {run.canary_store_id ? (
              <span className="text-[11px] text-amber-700 dark:text-amber-300" title="Το πρώτο κατάστημα της εκτέλεσης">
                καναρίνι: κατ. {run.canary_store_id}
              </span>
            ) : null}
          </button>
          {/* Η «Ακύρωση» είναι ΞΕΧΩΡΙΣΤΟ κουμπί, δίπλα και όχι μέσα στη γραμμή:
              κουμπί μέσα σε κουμπί δεν είναι έγκυρο και το κλικ θα άνοιγε
              ταυτόχρονα τις λεπτομέρειες. */}
          {canCancel && isLive(run) && (
            <button
              type="button"
              onClick={() => onCancel(run.id)}
              title="Σταματά την εκτέλεση μετά την τρέχουσα εργασία — ό,τι έχει ήδη σταλεί δεν αναιρείται"
              className="px-2.5 rounded-lg text-[11px] border border-red-300 dark:border-red-700/50 bg-red-500/10 text-red-700 dark:text-red-300 hover:bg-red-500/20"
            >
              Ακύρωση
            </button>
          )}
          </div>

          {selectedId === run.id && (
            <div className="mt-1 ml-6 border-l-2 border-line pl-3 space-y-2">
              {detailLoading && <Loader2 className="w-4 h-4 animate-spin text-t3" />}
              {detail && detail.id === run.id && (detail.jobs || []).map(job => (
                <div key={`${job.store_id}-${job.seq}`} className="border border-line rounded-lg p-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-t1">#{job.seq} {job.store_name || `κατ. ${job.store_id}`}</span>
                    {job.status && <span className="px-1.5 rounded bg-surface-3 text-t2">{job.status}</span>}
                    {job.blocked_reason && (
                      <span className="text-red-600 dark:text-red-400" title="Το interlock έφραξε την εργασία">
                        {job.blocked_reason}
                      </span>
                    )}
                    {job.items_before !== undefined && job.items_before !== null && (
                      <span
                        className={`text-t3 ${job.items_after !== undefined && job.items_after !== null && job.items_after !== job.items_before
                          ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}
                        title="Πλήθος ειδών πριν → μετά. Διαφορά σημαίνει ύποπτη διπλοεγγραφή."
                      >
                        είδη {job.items_before} → {job.items_after === undefined || job.items_after === null ? '—' : job.items_after}
                      </span>
                    )}
                    {!!job.codes_added && (
                      <span className="text-red-600 dark:text-red-400">{job.codes_added} νέοι κωδικοί</span>
                    )}
                  </div>
                  {(detail.calls || [])
                    .filter(c => c.job_id === job.id || (!c.job_id && (detail.jobs || []).length === 1))
                    .map((call, i) => (
                      <div key={`${call.id || i}`} className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-t3">
                        <span className="font-mono">{call.method || 'POST'}</span>
                        <span className="text-t2">{call.endpoint}</span>
                        {call.chunk && <span>chunk {call.chunk}</span>}
                        {call.rows !== undefined && call.rows !== null && <span>{call.rows} γρ.</span>}
                        {call.http_status !== undefined && call.http_status !== null && <span>HTTP {call.http_status}</span>}
                        {call.duration_ms !== undefined && call.duration_ms !== null && <span>{call.duration_ms} ms</span>}
                        <span className={OUTCOME_CLASSES[call.outcome || 'ok'] || ''}>
                          {call.outcome === 'unknown' ? 'έκβαση άγνωστη' : call.outcome}
                        </span>
                        {call.error && <span className="text-red-600 dark:text-red-400">{call.error}</span>}
                      </div>
                    ))}
                </div>
              ))}
              {detail && detail.id === run.id && (detail.jobs || []).length === 0 && !detailLoading && (
                <p className="text-xs text-t4">Καμία εργασία σε αυτή την εκτέλεση.</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

export default RunHistory;
